<div class="scrollable">
  <div class="content">
    <div class="container">
      <nav
        class="navbar is-fixed-top second-navbar-padding"
        role="navigation"
        aria-label="main navigation"
      >
        <div id="navbarBasicExample" class="navbar-menu">
          <div class="navbar-start self-align">
            <div class="navbar-item has-dropdown is-hoverable">
              <a
                class="navbar-link"
                href="documentation#documentation-workflow"
              >
                Workflow
              </a>

              <div class="navbar-dropdown">
                <a
                  class="navbar-item"
                  href="documentation#documentation-seed-genes"
                >
                  Seed Genes
                </a>
                <a
                  class="navbar-item"
                  href="documentation#documentation-algorithms"
                >
                  Algorithms
                </a>
              </div>
            </div>

            <div class="navbar-item has-dropdown is-hoverable">
              <a
                class="navbar-link"
                href="documentation#documentation-explorer"
              >
                Explorer
              </a>

              <div class="navbar-dropdown">
                <a
                  class="navbar-item"
                  href="documentation#documentation-left-menu"
                >
                  Left Menu
                </a>
                <a
                  class="navbar-item"
                  href="documentation#documentation-network"
                >
                  Network
                </a>
                <a
                  class="navbar-item"
                  href="documentation#documentation-analysis"
                >
                  Analysis View
                </a>
              </div>
            </div>

            <a
              class="navbar-item"
              href="documentation#documentation-drug-interaction-lookup"
            >
              Drug Lookup
            </a>

            <a
              class="navbar-item"
              href="documentation#documentation-gene-interaction-lookup"
            >
              Gene Lookup
            </a>

            <!-- <div class="navbar-item has-dropdown is-hoverable">
              <a class="navbar-link"> Analysis View </a>

              <div class="navbar-dropdown">
                <a class="navbar-item"> About </a>
                <a class="navbar-item"> Jobs </a>
                <a class="navbar-item"> Contact </a>
                <hr class="navbar-divider" />
                <a class="navbar-item"> Report an issue </a>
              </div>
            </div> -->
            <span class="navbar-item"> Version: 1.1.0 </span>
          </div>
        </div>
      </nav>
    </div>

    <div class="hero container">
      <div class="column is-8 is-offset-2">
        <div class="container has-text-centered">
          <img
            src="assets/caddie_logo.png"
            alt="CADDIE logo"
            title="CADDIE logo"
          />
          <h2 class="subtitle is-3">How to use CADDIE</h2>
        </div>
        <div class="content">
          <p>
            CADDIE (Cancer Driver Drug Interaction Explorer) is a resource for
            drug repurposing in cancer based on gene-gene and drug-gene
            interaction (GGI and DGI) networks. The user has the option to
            choose between the implemented interaction networks from STRING,
            BioGRID, DrugBank, ChEMBL, and more. CADDIE then exploits the
            information in selected networks using network algorithms like
            TrustRank or Degree Centrality. The algorithms and the desired
            parameters are again customizable by the user. By doing this, CADDIE
            can identify drug repurposing candidates or to search for putative
            drug targets.
          </p>
          <a id="documentation-workflow"></a>
          <h4 class="title is-4">Workflow</h4>
          <p>The main steps of working with CADDIE are:</p>
          <ol>
            <li>Pick your cancer type of interest</li>
            <li>Select the gene-interaction database of your choice</li>
            <li>
              Find
              <a href="documentation#documentation-seed-genes">seed genes</a>
            </li>
            <li>
              Start a drug-target or drug search using one of the implemented
              <a href="documentation#documentation-algorithms">algorithms</a>
            </li>
            <li>
              Visualize the results and download them for further processing
            </li>
          </ol>
          <p>
            Variations of this workflow are of course possible, for example, you
            have the option to upload your list of
            <a href="documentation#documentation-seed-genes">seed genes</a> or
            to find
            <a href="documentation#documentation-seed-genes">seed genes</a>
            based on related diseases. In general, CADDIE facilitates the seed
            selection process with a variety of different types of implemented
            information in the
            <a href="documentation#documentation-explorer">explorer</a>.
          </p>

          <a id="documentation-seed-genes"></a>

          <h4 class="title is-4">Seed genes</h4>
          <p>
            Seed genes are the starting genes for the algorithms. All
            implemented algorithms search in the interaction network for
            drug-targets or drugs based on their relation the seed genes.
            Therefore, the seed genes are your genes of interest for which
            putative drugs or drug-target candidates should be found for. The
            easiest way to select a seed gene is by double-clicking on a node in
            the network.
            <br />
            CADDIE also offers the possibility to load seed genes directly from
            the cancer types in the database. A cancer type can be selected in
            the left side bar and the menu button in the bottom of the network
            contains the function "Add cancer driver genes", which adds all the
            cancer genes in the network to the seed nodes. Further it is
            possible to define seed genes based on the related diseases in the
            left side bar by clicking on the plot or by uploading a custom list
            of gene names, uniprot ACs or entrez IDs when by cklicking of
            "Custom Genes" in the menu or in the selection panel in the left
            side bar.
          </p>

          <div class="container has-text-centered">
            <img
              src="assets/Upload Genes Manual.png"
              alt="Upload a list of genes to CADDIE"
              title="Upload a list of genes to CADDIE"
            />
          </div>
          
          <a id="documentation-algorithms"></a>

          <h4 class="title is-4">Algorithms</h4>
          <p>
            CADDIE comes with algorithms for both drug-target identification and
            drug candidate search. Drug-target algorithms give you the
            possibility to find hub genes in the human gene-interaction network
            that are central to your seed genes. They also can be a helpful
            preprocessing step for your drug search. We implemented
            <b>6 different drug-target search algorithms in CADDIE</b>:
          </p>
          <ul>
            <li>
              <b>Mutli-Steiner Tree:</b> The Multi-level Steiner tree algorithm
              is used to approximate the minimum spanning tree spanned by the
              seed nodes in a reasonable time. The implementation is adopted
              from
              <a href="https://arxiv.org/abs/1804.02627" target="_blank"
                >Ahmed et al.</a
              >
              It can be used to create a minimum spanning subnetwork between
              user-selected seed nodes, which happen to be central interaction
              partners between the seed nodes, and thus represent favorable
              drug-targets.
              <ul>
                <li>
                  <i>Number of Steiner trees:</i> Number of computed Steiner
                  trees to approximate minimum spanning tree. A higher number
                  leads to more variations at the cost of a longer runtime.
                </li>
                <li>
                  <i>Tolerance:</i> Indicates how much the subsequent trees may
                  increase the number of edges.
                </li>
              </ul>
            </li>
            <li>
              <a
                href="https://exbio.wzw.tum.de/keypathwayminer/"
                target="_blank"
                ><b>KeyPathwayMiner:</b></a
              >
              KeyPathwayMiner (KPM) is an online tool developed by
              <a
                href="https://academic.oup.com/nar/article/44/W1/W98/2499355"
                target="_blank"
                >Alcaraz et al.</a
              >
              for pathway enrichment analysis. Users have the option to utilize
              KPM for their drug-target search by selecting seed nodes in the
              GGI network and letting KPM find an interaction network of genes
              that are commonly dysregulated spanned by the seed genes. The
              resulting proteins are presumably functionally related to the seed
              nodes, and therefore are suitable drug-target candidates. Only one
              parameter K has to be set by the user which defines the amount of
              permitted intermediate nodes that are neither part of the seed
              nodes nor the common pathway.
              <ul>
                <li>
                  <i>Additional proteins (k):</i> Number of intermediate nodes
                  that are allowed in between the seed nodes.
                </li>
              </ul>
            </li>
            <li>
              <b>TrustRank: </b> TrustRank is based on the same concepts as the
              <a href="http://ilpubs.stanford.edu:8090/422/" target="_blank"
                >Google PageRank algorithm</a
              >
              and Closeness Centrality. A crawler searches the network based on
              user-selected seeds and ranks visited nodes, damping the score
              based on the distance traveled. The damping factor can be set by
              the user in a range from 0-1, with a higher damping factor causing
              the crawler to explore nodes in close proximity or in larger
              portions of the GGI network. In CADDIE, TrustRank is able to find
              putative drug targets as well as drug candidates.
              <ul>
                <li>
                  <i>Damping Factor:</i> The speed with which the crawler
                  propagates through the network. The larger the factor, the
                  larger the proportion of the network that is considered.
                </li>
              </ul>
            </li>
            <li>
              <b>Degree Centrality:</b> Degree centrality measurement is
              obtained by ranking the nodes in a network based on their degree,
              which is defined as the number of neighbors a node has divided by
              the number of nodes. It be described as
              <div class="documentation-formula">
                <i>centrality<sub>degree</sub>(x) = deg(x)</i>
              </div>
              where x is a given node and deg(x) is its degree. While it is a
              <a
                href="https://pubmed.ncbi.nlm.nih.gov/21575741/"
                target="_blank"
                >commonly used network analysis technique</a
              >, it most importantly has been shown useful in the
              <a
                href="https://pubmed.ncbi.nlm.nih.gov/26355787/"
                target="_blank"
                >identification of essential proteins in PPI networks</a
              >. Thus, it is a simple approach to classifying the
              network-related importance of a particular protein. In CADDIE it
              can be used to discover valuable drug-targets or drugs, based on
              the seed selection given by the user.
            </li>
            <li>
              <b>Harmonic Centrality:</b> Harmonic centrality measurement can be
              described as the average shortest distance from each node to all
              other nodes in a network. This measurement is the equivalent of
              closeness centrality for disconnected graphs. Formally speaking, it
              can be annotated as

              <div class="documentation-formula">
                <i>centrality<sub>harmonic</sub>(x) = </i>&sum;<sub
                  >y&#x2260;x</sub
                ><sup>y</sup>( 1 / dist(x, y) )
              </div>

              where x is a given node and 1/dist(x, y) = 0 if dist(x, y) = ∞
              (<a
                href="https://www.semanticscholar.org/paper/Closeness-Centrality-Extended-to-Unconnected-the-Rochat/a4ea7b92421415674c6ed954914f0512935d0dfb"
                target="_blank"
                >reference</a
              >). The closer a node is to other nodes, the higher the score. It
              has already been proven successful in a number of biological
              network problems for instance with
              <a
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2733090/"
                target="_blank"
                >metabolic</a
              >
              or
              <a
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4999434/"
                target="_blank"
                >PPI networks</a
              >
              . In CADDIE, this algorithm can be used to find drug targets or
              drugs that affect the seed nodes as direct as possible.
            </li>
            <li>
              <b>Betweenness Centrality:</b> Betweenness is obtained by finding
              the shortest paths for each pair of nodes in the network and
              assessing the number of shortest paths that pass through a
              particular node, such that a measure of the centrality of a node
              in a network global context is received. Betweenness Centrality
              has been established as a common measurement in
              <a
                href="https://bmcbioinformatics.biomedcentral.com/articles/10.1186/1471-2105-7-219"
                target="_blank"
                >network biological</a
              >
              application and is especially practical in
              <a href="https://www.pnas.org/content/99/12/7821" target="_blank"
                >finding communities in large networks</a
              >. In CADDIE, betweenness is based on the shortest paths between
              the seed nodes only and can be used to find drug targets with
              maximized connectivity to all seeds.
            </li>
          </ul>

          <p>
            Furthermore, we integrated
            <b>4 drug candidate search algorithms.</b> These tasks will return a
            list of drugs and their relations to your seed genes.
          </p>

          <ul>
            <li><b>TrustRank:</b> See description above.</li>
            <li><b>Harmonic Centrality:</b> See description above.</li>
            <li><b>Degree Centrality:</b> See description above.</li>
            <li>
              <b>Network Proximity:</b> Network Proximity, as introduced by
              <a
                href="https://www.nature.com/articles/ncomms10331"
                target="_blank"
                >Guney et al.</a
              >, is the average length of shortest paths from drug target nodes
              to all of the user-selected seed nodes. The algorithm then
              computes a statistical significance score comparing against random
              expectation. This algorithm was adopted in CADDIE so that
              best-scored drugs are returned to the user as candidate drugs.
            </li>
          </ul>

          <p>
            All of the algorithms can be adjusted to your needs with parameters.
            We also provide custom options such as filtering out unapproved or
            nutraceutical drugs. In general, the following options exist:
          </p>

          <ul>
            <li>
              <i>Result Size: </i>Number of returned results (drug-targets or
              drugs)
            </li>
            <li>
              <i>Maximum Degree: </i>Option to filter out hub genes. if set to >
              0, genes with a network degree in the complete gene-drug
              interaction network above this threshold will be excluded.
            </li>
            <li>
              <i>Hub Penalty: </i>Penalize genes with a large degree in the
              network. The higher the value, the more algorithms will try to
              avoid genes with a high network degree in the complete gene-drug
              interaction network.
            </li>
            <li>
              <i>Mutation Weights: </i>Select a cancer type to consider its
              mutations. The algorithm will favor genes with a larger mutation
              score.
            </li>
            <li>
              <i>Expression Weights: </i>Select a cancer type to consider its
              expressions. The algorithm will favor genes with a higher
              expression.
            </li>
            <li>
              <i>Filter Edges: </i>If set, only the shortest paths to the
              results (drug-target genes or drugs) will be displayed in the
              resulting network. Otherwise, all found pathways will be shown.
            </li>
            <li>
              <i>Gene Drug Interactions (only drug search): </i>Choose a data
              resource for the drug-gene interactions. If 'DrugBank" is
              selected, the options 'Drug Target Actions' will appear.
            </li>
            <li>
              <i>Drug Target Actions (only drug search): </i>Only available for
              gene-drug interaction dataset 'DrugBank'. Filter out drug-gene
              edges by known effect.
            </li>
            <li>
              <i>Nutraceutical Drugs (only drug search): </i>If set,
              nutraceutical drugs will be excluded.
            </li>
            <li>
              <i>Only CTRPv2 (only drug search): </i>If set, only drugs tested
              in the
              <a
                href="https://portals.broadinstitute.org/ctrp/?page=#ctd2BodyHome"
                target="_blank"
                >CTRPv2</a
              >
              dataset are considered. The CTRPv2 study contains 481 compounds
              that target distinct processes in the cell.
            </li>
            <li>
              <i>Only ATC class L (only drug search): </i>If set, only drugs
              that are listed in the
              <a
                href="https://www.whocc.no/atc_ddd_index/?code=L&showdescription=yes"
                target="_blank"
                >ATC class L Antineoplastic and immunomodulating agents</a
              >
              by the
              <a href="https://www.who.int/" target="_blank"
                >World Health Organization (WHO)</a
              >.
            </li>
          </ul>

          <a id="documentation-explorer"></a>
          <h4 class="title is-4">Explorer</h4>
          <p>
            The Explorer is the main part of the application. It allows you to
            explore the interactome of your cancer type or cancer driver genes
            of interest. Most importantly, it gives you a variety of information
            like related diseases or expression values to help you select seed
            genes for your drug or drug-target search.
          </p>
          <a id="documentation-left-menu"></a>
          <h4 class="title is-5">Left Menu</h4>
          <p>
            On the left side, you can find a menu bar that gives you the options
            to select your cancer type or gene interaction resource of interest.
            You will receive a short overview of your cancer type of interest.
            After selecting your
            <a href="documentation#documentation-seed-genes">seed genes</a>, the
            left menu allows you to start analysis tasks (max. 3 at a time). The
            tasks will be displayed at the bottom of this menu in the 'Tasks'
            section. A click on a finished task leads you to the analysis view.
          </p>
          <div class="container has-text-centered">
            <img
              src="assets/CADDIE left side menu.png"
              width="500"
              alt="CADDIE Explorer left side menu"
              title="Description of the left side menu of the CADDIE explorer "
            />
          </div>
          <a id="documentation-network"></a>
          <h4 class="title is-5">Gene-Interaction Network</h4>
          <p>
            On the right side of the explorer is the gene-interaction network.
            It contains all cancer driver genes of your selected cancer type(s),
            forming a minimal spanning tree in the human gene interactome. It is
            supplemented by genes found by the Multiple Steiner Tree algorithm,
            which was used to pre-compute the network. Therefore, the network
            will change when a new interaction dataset is selected. The network
            gives you an overview of the relations of the cancer driver genes to
            each other and lets you select
            <a href="documentation#documentation-seed-genes">seed genes</a> for
            the drug-target or drug search by double-clicking the nodes. A
            single click on a node opens a window at the top of the left menu
            with detailed information about the drug or gene such as related
            diseases or the node degree in the complete human drug-gene
            interaction network.
          </p>
          <div class="container has-text-centered">
            <img
              src="assets/annotated network.png"
              alt="Gene-interaction network with cancer driver genes"
              title="Gene-interaction network with higlighted cancer driver genes and menu"
            />
          </div>
          <p>
            Below the network, you can find various functions that help you to
            manipulate the network to find genes of your interest. The
            expression of genes in different tissues (data from GTEx) as well as
            in different cancer types (data from TCGA) can be highlighted and
            genes may be selected automatically above a certain threshold. The
            same possibility exists for mutation data from TCGA. Furthermore, a
            menu is available with versatile options such as selection of all
            cancer driver genes or upload of a vcf-file to filter for genes with
            damaging mutations.
            <br />
            You can add your own genes to the network by uploading your own seed
            genes (for example via the 'Custom genes' function in the left menu)
            followed by the 'Add to network' functionality. Gene-gene
            interactions are taken from the selected interaction database.
          </p>
          <a id="documentation-analysis"></a>
          <h4 class="title is-5">Analysis View</h4>
          <p>
            The analysis view opens when you click on a finished task in the
            left menu in the explorer. The analysis view will open on top of the
            network and is divided into four parts. One part is the resulting
            network, which offers the same functions as the initial gene network
            but here it displays all
            <a href="documentation#documentation-seed-genes">seed genes</a> in
            combination with the found genes and/or drugs and allows you to
            inspect the relations from the seeds to the results.
          </p>
          <div class="container has-text-centered">
            <img
              src="assets/CADDIE analysis network.png"
              alt="CADDIE analysis network"
              title="CADDIE analysis network"
            />
          </div>
          <p>
            You can also look at your results in a table format, where
            additional information to each of the genes or drugs is displayed.
            Here you can sort the nodes to get a better overview and download
            the tables for further processing on your side. Furthermore, CADDIE
            also provides different statistics and graphs about the analysis
            (depending on your analysis parameters). For example, you can set
            the calculated score for each of your genes and drugs in comparison
            to the node degrees in the drug-gene interactome (see below).
            Ultimately, the selected parameters and versions of the used
            datasets can be retrieved in the fourth tab.
          </p>
          <div class="container has-text-centered">
            <img
              src="assets/plot score vs degree.png"
              alt="CADDIE score drug-gene interaction network degree"
              title="CADDIE score compared to the drug-gene interaction network degree"
            />
          </div>
          <a id="documentation-drug-interaction-lookup"></a>
          <h4 class="title is-4">Drug Interaction Lookup</h4>
          <p>
            The
            <a href="drug-lookup" target="_blank">drug interaction lookup</a>
            enables you to inspect the interaction datasets in respect to
            individual drugs. If the searched drug is not found in the database,
            the drug with the closest matching name will be shown.
            <br />
            All interacting genes based on the selected drug-gene interaction
            dataset will be listed. Cancer driver genes for any cancer type in
            the CADDIE database will be highlighted.
          </p>
          <div class="container has-text-centered">
            <img
              src="assets/drug_interaction_lookup_annotated.png"
              alt="CADDIE drug interaction lookup"
              title="CADDIE drug interaction lookup page"
            />
          </div>
          <a id="documentation-gene-interaction-lookup"></a>
          <h4 class="title is-4">Gene Interaction Lookup</h4>
          <p>
            The
            <a href="gene-lookup" target="_blank">gene interaction lookup</a>
            works similar to the
            <a href="documentation#documentation_drug_interaction_lookup"
              >drug interaction lookup</a
            >
            with the difference that it allows you to search for genes instead
            of drugs. The list of drug interactions will be shown along with
            detailed drug information. In addition, you will receive a list of
            all known cancer types related to your gene of interest.
          </p>

          <a id="documentation-browser_compatability"></a>
          <h4 class="title is-4">Browser Compatibility</h4>
          <div class="container has-text-centered">
            <table>
              <tr>
                <th>OS</th>
                <th>Version</th>
                <th>Chrome</th>
                <th>Firefox</th>
                <th>Microsoft Edge</th>
                <th>Safari</th>
                <th>Opera</th>
              </tr>
              <tr>
                <td>Linux</td>
                <td>Ubuntu 16.04</td>
                <td>97.0</td>
                <td>96.0</td>
                <td>n/a</td>
                <td>n/a</td>
                <td>82.0</td>
              </tr>
              <tr>
                <td>MacOS</td>
                <td>Monterey</td>
                <td>97.0</td>
                <td>96.0</td>
                <td>n/a</td>
                <td>15.1</td>
                <td>82.0</td>
              </tr>
              <tr>
                <td>Windows</td>
                <td>10</td>
                <td>97.0</td>
                <td>96.0</td>
                <td>95.0</td>
                <td>n/a</td>
                <td>82.0</td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
